import { Box, Table, TableBody, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  StatusChip,
} from '../../../common/components';
import { Edit as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { parseAmountInput } from '../utils/paymentOrdersUtils';

const resolveAccountLabel = (movement, bankAccounts) => {
  const account = bankAccounts.find((acc) => acc.uuid === movement.uuid_bank_account);
  if (!account) return '-';

  const bankName = account.bank_name ? account.bank_name.replace(/_/g, ' ') : '';
  const baseLabel = `${bankName} - ${account.account_number}`;

  return movement.concept ? `${baseLabel}\n${movement.concept}` : baseLabel;
};

const resolveMovementType = (movement, movementTypes) => {
  return movementTypes.find((type) => type.uuid === movement.uuid_movement_type)?.description || '-';
};

const resolveDisplayAmount = (movement) => {
  const numeric = parseAmountInput(movement.amount);
  if (numeric === null) {
    return 0;
  }
  const sign =
    movement.amountSign != null ? movement.amountSign : numeric < 0 ? -1 : 1;
  return Math.abs(numeric * sign);
};

const PaymentOrderMovementsTable = ({
  movements = [],
  movementTypes = [],
  bankAccounts = [],
  onEdit,
  onRemove,
  getMovementKey,
}) => {
  const theme = useTheme();

  return (
    <GeneralTableBox sx={{ mt: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <HeadTableCell>Tipo mov.</HeadTableCell>
            <HeadTableCell>Cuenta</HeadTableCell>
            <HeadTableCell>Num. comprob.</HeadTableCell>
            <HeadTableCell>Fecha emi.</HeadTableCell>
            <HeadTableCell>Fecha venc.</HeadTableCell>
            <HeadTableCell align="right">Importe</HeadTableCell>
            <HeadTableCell align="center">Cheque de canje</HeadTableCell>
            <HeadTableCell align="center">Conciliado</HeadTableCell>
            <HeadTableCell align="center">Acciones</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {movements.map((movement) => {
            const isReconciled = Boolean(movement.is_reconciled);
            const rowKey = getMovementKey(movement);

            return (
              <TableRow
                key={rowKey}
                hover
                sx={{
                  backgroundColor: isReconciled
                    ? 'rgba(76, 175, 80, 0.08)'
                    : 'inherit',
                  borderLeft: isReconciled
                    ? `4px solid ${theme.palette.success.main}`
                    : undefined,
                }}
              >
                <RowTableCell>{resolveMovementType(movement, movementTypes)}</RowTableCell>
                <RowTableCell>
                  <Box sx={{ whiteSpace: 'pre-line' }}>
                    {resolveAccountLabel(movement, bankAccounts)}
                  </Box>
                </RowTableCell>
                <RowTableCell>{movement.voucher_number || '-'}</RowTableCell>
                <RowTableCell>{movement.emission_date ? formatDate(movement.emission_date) : '-'}</RowTableCell>
                <RowTableCell>{movement.due_date ? formatDate(movement.due_date) : '-'}</RowTableCell>
                <RowTableCell align="right">
                  {formatCurrency(resolveDisplayAmount(movement), 2)}
                </RowTableCell>
                <RowTableCell align="center">
                  {movement.display_in_book ? (
                    <StatusChip label="No" color="success" size="small" />
                  ) : (
                    <StatusChip label="Si" color="warning" size="small" />
                  )}
                </RowTableCell>
                <RowTableCell align="center">
                  {isReconciled ? (
                    <StatusChip label="Si" color="success" size="small" />
                  ) : (
                    <StatusChip label="No" color="warning" size="small" />
                  )}
                </RowTableCell>
                <RowTableCell align="center">
                  <Tooltip
                    title={
                      isReconciled
                        ? 'No se puede editar un movimiento conciliado'
                        : 'Editar movimiento'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEdit(movement)}
                        disabled={isReconciled}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isReconciled
                        ? 'No se puede eliminar un movimiento conciliado'
                        : 'Eliminar movimiento'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onRemove(movement)}
                        disabled={isReconciled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </RowTableCell>
              </TableRow>
            );
          })}
          {!movements.length && (
            <TableRow>
              <RowTableCell colSpan={9} align="center">
                Aún no se han agregado movimientos.
              </RowTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default PaymentOrderMovementsTable;
